import { AimOutlined } from "@ant-design/icons";
import { keyOf } from "@p0security/shared/graph/graph";
import { ConnectedNode, Node, NodeOf } from "@p0security/shared/graph/types";
import { Grid, Spacing } from "@p0security/ui";
import { widetype } from "@p0security/util/collections";
import {
  ControlButton,
  Controls,
  Edge as FlowEdge,
  Node as FlowNode,
  Position as FlowPosition,
  Handle,
  MarkerType,
  MiniMap,
  NodeMouseHandler,
  NodeProps,
  Panel,
  ReactFlow,
  ReactFlowInstance,
} from "@xyflow/react";

import "@xyflow/react/dist/style.css";

import classNames from "classnames";
import { capitalize, uniqBy } from "lodash";
import React, {
  CSSProperties,
  ComponentType,
  MouseEventHandler,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";

import { NodeDrawer } from "./node/NodeDrawer";

type Position = {
  /** Pixel x-coordinate of this node */
  x: number;
  /** Pixel y-coordinate of this node */
  y: number;
};

type Size = {
  height: number;
  width: number;
};

export type NodeHighlight = {
  highlight?: boolean;
};

export type NodeRenderProps<G extends object, K extends keyof G, P> = {
  content: (node: ConnectedNode<G, K>, extra: P) => ReactNode;
  icon: ReactNode;
  size: (node: ConnectedNode<G, K>, extra: P) => Size;
  style: (node: ConnectedNode<G, K>) => {
    [K in "backgroundColor" | "borderColor"]: NonNullable<CSSProperties[K]>;
  };
  title: (node: Node<G, K>, extra: P) => ReactNode;
};

export type NodeFlowRenderer<G extends object, P> = {
  [K in keyof G]: NodeRenderProps<G, K, P>;
};

export type NodeFlowSpec<A extends object> = Size & {
  inner: ConnectedNode<A, keyof A>;
  display: "inner" | "leaf" | "root";
  position: Position;
};

export type NodeFlowEdge<A extends object> = {
  parent: ConnectedNode<A, keyof A>;
  child: ConnectedNode<A, keyof A>;
};

export type NodePropertiesRenderer<A extends object> = (
  node: NodeOf<A>
) => ReactNode;

export type FitViewOptions = Partial<{
  minZoom: number;
  maxZoom: number;
  duration: number;
  padding: number;
}>;

const MARKER_SIZE = 24;

/** The outer frame of a graph node, sized to the dimensions the layout
 * calculated for it. Those dimensions are only known at render time, so they
 * stay inline styles; everything fixed about the frame is a utility class. */
export const NodeDiv: React.FC<PropsWithChildren<Size>> = ({
  children,
  height,
  width,
}) => (
  <div
    className="flex flex-col items-stretch overflow-clip"
    style={{ height, width }}
  >
    {children}
  </div>
);

const NodeHeaderDiv: React.FC<PropsWithChildren> = ({ children }) => (
  <div className="block overflow-clip text-ellipsis text-nowrap">
    {children}
  </div>
);

export const NODE_BORDER_WIDTH = 1;

/** The body of a graph node. Its fill and border are chosen per node type, and
 * its width bound comes from the layout, so both remain inline styles. */
export const NodeBodyDiv: React.FC<
  PropsWithChildren<{
    backgroundColor: string;
    borderColor: string;
    width: number;
    style?: CSSProperties;
    onClick?: MouseEventHandler<HTMLDivElement>;
  }>
> = ({ backgroundColor, borderColor, children, onClick, style, width }) => (
  <div
    className="relative box-border flex grow items-center overflow-clip rounded-[5px] border-solid p-2"
    style={{
      backgroundColor,
      borderColor,
      borderWidth: NODE_BORDER_WIDTH,
      maxWidth: width,
      ...style,
    }}
    onClick={onClick}
  >
    {children}
  </div>
);

export const NodeContentDiv: React.FC<PropsWithChildren<NodeHighlight>> = ({
  children,
  highlight,
}) => (
  <div
    className={classNames("w-full overflow-clip text-nowrap", {
      "bg-p0-highlight": highlight,
    })}
  >
    {children}
  </div>
);

const GRAPH_BACKGROUND =
  "radial-gradient(ellipse, transparent, #fafcff 70%), linear-gradient(130deg, transparent 75%, rgba(0, 135, 68, .26) 54%, rgba(0, 135, 68, .26) 60%, transparent 70%), linear-gradient(112deg, transparent 35%, rgba(145, 235, 0, .15) 43%, rgba(145, 235, 0, .15) 0, transparent 65%)";

export const GraphNode = <A extends object, K extends keyof A, P>({
  context,
  data,
  renderers,
  sourcePosition,
  targetPosition,
  type,
}: NodeProps & {
  context: React.Context<P>;
  data: { node: ConnectedNode<A, K & string> & NodeHighlight };
  renderers: NodeFlowRenderer<A, P>;
  type: K;
}) => {
  const { node } = data;
  const args = useContext(context);
  const render = useMemo(() => renderers[type], [renderers, type]);
  const body = useMemo(() => render.content(node, args), [args, node, render]);
  const title = useMemo(() => render.title(node, args), [args, node, render]);
  const size = useMemo(() => render.size(node, args), [args, node, render]);
  const style = useMemo(() => render.style(node), [render, node]);

  return (
    <NodeDiv {...size}>
      <NodeHeaderDiv>
        <span style={{ fontSize: "18px" }}>{render.icon}</span>
        <span style={{ fontWeight: 500, fontSize: "14px" }}>{title}</span>
      </NodeHeaderDiv>
      <NodeBodyDiv
        borderColor={style.borderColor}
        backgroundColor={style.backgroundColor}
        {...size}
      >
        <NodeContentDiv highlight={node.highlight}>{body}</NodeContentDiv>
        {targetPosition ? (
          <Handle type="target" position={targetPosition} />
        ) : null}
        {sourcePosition ? (
          <Handle type="source" position={sourcePosition} />
        ) : null}
      </NodeBodyDiv>
    </NodeDiv>
  );
};

function generateVisualizationNode<A extends object, P>(
  type: keyof A,
  context: React.Context<P>,
  renderers: NodeFlowRenderer<A, P>
) {
  const Component: ComponentType<
    NodeProps & {
      data: { node: ConnectedNode<A, keyof A & string> };
      type: keyof A;
    }
  > = ({ ...props }) => (
    <GraphNode<A, keyof A, P>
      context={context}
      renderers={renderers}
      {...props}
    />
  );
  Component.displayName = `${capitalize(String(type))}Node`;
  return Component;
}

export type GraphVisualizationInstance<A extends object> = {
  selectNode: (node: NodeOf<A> | undefined) => void;
};

/** Renders a graph of nodes as a network visualization */
export const GraphVisualization = <A extends object, P>(props: {
  describer: NodePropertiesRenderer<A>;
  edges: NodeFlowEdge<A>[];
  fitView?: boolean;
  fitViewOptions?: FitViewOptions;
  minimap?: boolean;
  nodes: NodeFlowSpec<A>[];
  onInit?: (instance: GraphVisualizationInstance<A>) => void;
  renderContext: React.Context<P>;
  renderer: NodeFlowRenderer<A, P>;
  viewStyle?: React.CSSProperties;
}) => {
  const {
    edges,
    fitView,
    fitViewOptions,
    minimap,
    nodes,
    onInit,
    renderContext,
    renderer,
    viewStyle,
    ...drawerProps
  } = props;
  const breakpoints = Grid.useBreakpoint();
  const [selectedNode, setSelectedNode] = useState<NodeOf<A>>();
  const [flowInstance, setFlowInstance] = useState<ReactFlowInstance>();

  const [renderKey, setRenderKey] = useReducer((x) => x + 1, 0);

  const [showOverlay, setShowOverlay] = useState(false);
  const overlayTimer = useRef<number | null>(null);
  const [spaceDown, setSpaceDown] = useState(false);

  // allow tracking of space key down to disable overlay
  // this is necessary because the spacekey isn't tracked
  // as a modifier key by the wheel event
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Space") {
        setSpaceDown(true);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === "Space") {
        setSpaceDown(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  // re-render our custom fit view when triggered
  const handleFitView = useCallback(() => {
    setRenderKey();
  }, [setRenderKey]);

  const toFlowNode = useMemo(() => {
    return (spec: NodeFlowSpec<A>): FlowNode => {
      const { inner, display, position, width, height } = spec;
      return {
        id: keyOf(inner),
        type: inner.type.toString(),
        data: {
          node: inner,
          spec,
        },
        position: {
          x: position.x,
          y: position.y,
        },
        sourcePosition:
          display === "leaf" ? FlowPosition.Left : FlowPosition.Right,
        targetPosition:
          display === "root" ? FlowPosition.Right : FlowPosition.Left,
        width: width,
        height: height,
      };
    };
  }, []);

  const flowNodes: FlowNode[] = useMemo(
    () => uniqBy(nodes.map(toFlowNode), "id"),
    [nodes, toFlowNode]
  );

  const flowEdges: FlowEdge[] = useMemo(
    () =>
      uniqBy(
        edges.map<FlowEdge>(({ child, parent }) => ({
          id: `${keyOf(parent)}-${keyOf(child)}`,
          source: keyOf(parent),
          target: keyOf(child),
          markerEnd: {
            type: MarkerType.ArrowClosed,
            height: MARKER_SIZE,
            width: MARKER_SIZE,
          },
        })),
        "id"
      ),
    [edges]
  );

  const instance = useMemo<GraphVisualizationInstance<A>>(
    () => ({ selectNode: setSelectedNode }),
    [setSelectedNode]
  );

  // React flow abandons edges on the graph on update
  // Force clear to ensure that zombie edges do not remain on search update
  useEffect(() => {
    flowInstance?.setEdges([]);
  }, [flowEdges, flowInstance]);

  useEffect(() => {
    onInit?.(instance);
  }, [instance, onInit]);

  const closeNodeDrawer = useCallback(() => setSelectedNode(undefined), []);
  const handleNodeClick: NodeMouseHandler<FlowNode> = useCallback(
    (_, node) => {
      const spec = nodes.find((n) => keyOf(n.inner) === node.id);
      if (spec) setSelectedNode?.(spec.inner);
    },
    [nodes, setSelectedNode]
  );

  // The size of the parent containers depends on the breakpoints, and this component fills the entire parent container.
  // So if the breakpoints are not yet known, ReactFlow will mount before the parent container has been correctly sized.
  //
  // useBreakpoint() initially returns an empty object, and forces a second render to update the breakpoints right afterwards.
  // See useBreakpoint() implementation: https://github.com/ant-design/ant-design/blob/master/components/grid/hooks/useBreakpoint.tsx
  // i.e. it initially returns {}, but then in the second render returns { xs: true, md: true, lg: false, ... }
  //
  // This is not normally a problem, because after the second render, typical use cases will automatically use the updated breakpoints
  // to re-render components with the correct breakpoint information.
  //
  // However, React Flow measures the size of its parent container in order to determine the initial position of its viewport,
  // (See https://reactflow.dev/learn/troubleshooting#the-react-flow-parent-container-needs-a-width-and-a-height-to-render-the)
  // This occurs the first time the <ReactFlow /> component is rendered, and may cause ReactFlow to incorrectly calculate the initial viewport position,
  // leading to a suboptimal initial view of the graph.
  //
  // To fix this, we force a re-render of the component after the breakpoints have been updated.

  const onWheelCapture = useCallback(
    (event: React.WheelEvent) => {
      const ctrl = event.ctrlKey;
      const space = spaceDown;

      if (!(ctrl || space) || !flowInstance) {
        setShowOverlay(true);
        if (overlayTimer.current) window.clearTimeout(overlayTimer.current);
        overlayTimer.current = window.setTimeout(() => {
          setShowOverlay(false);
        }, 1000);
      }
    },
    [flowInstance, spaceDown]
  );

  const nodeTypes = useMemo(
    () =>
      widetype.mapValues(renderer, (_, type) =>
        generateVisualizationNode(type, renderContext, renderer)
      ),
    [renderContext, renderer]
  );

  if (Object.entries(breakpoints).length === 0) {
    return null;
  }

  return (
    <div
      style={{
        backgroundColor: "rgb(250, 252, 255)",
        backgroundImage: GRAPH_BACKGROUND,
        // Necessary for inline NodeDrawer; see https://4x.ant.design/components/drawer/
        position: "relative",
        height: viewStyle?.height ?? "100%",
        width: viewStyle?.width ?? "100%",
        ...viewStyle,
      }}
      onWheelCapture={onWheelCapture}
    >
      <ReactFlow
        key={renderKey}
        edges={flowEdges}
        fitView={fitView}
        fitViewOptions={fitViewOptions}
        // Allows viewing very large identity graphs in one screen, anything smaller just
        // looks like noise
        minZoom={5e-2}
        nodes={flowNodes}
        nodesConnectable={false}
        nodeTypes={nodeTypes}
        onInit={setFlowInstance}
        onNodeClick={handleNodeClick}
        preventScrolling={false}
        zoomActivationKeyCode={"Control"}
        zoomOnPinch={true}
      >
        <Spacing asChild m={12}>
          <Panel position="top-right">
            {showOverlay && (
              <div
                style={{
                  padding: "6px 10px",
                  background: "rgba(0,0,0,0.75)",
                  color: "white",
                  borderRadius: 4,
                  fontSize: 12,
                  pointerEvents: "none",
                }}
              >
                Hold CTRL to zoom
                <br />
                or SPACE to pan
              </div>
            )}
          </Panel>
        </Spacing>
        {minimap && <MiniMap zoomable pannable />}
        <Controls showInteractive={false} showFitView={false}>
          <ControlButton onClick={handleFitView}>
            <AimOutlined />
          </ControlButton>
        </Controls>
      </ReactFlow>
      {selectedNode?.type && selectedNode.type !== "_collapsed" ? (
        <NodeDrawer<A, P>
          onClose={closeNodeDrawer}
          node={selectedNode}
          renderContext={renderContext}
          renderer={renderer}
          {...drawerProps}
        />
      ) : null}
    </div>
  );
};
